import React, { useState, useEffect } from 'react';
import { Card, Button, Modal, Form, Alert } from 'react-bootstrap';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

const FolderManager = ({ studyCircleId, onFoldersUpdate }) => {
    const [folders, setFolders] = useState([]);
    const [studyCircles, setStudyCircles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [currentFolder, setCurrentFolder] = useState({
        name: '',
        study_circle_id: '',
        description: ''
    });
    const [error, setError] = useState('');
    const { user } = useAuth();
    const [isCreating, setIsCreating] = useState(false);
    const [isEditing, setIsEditing] = useState(false);

    useEffect(() => {
        if (studyCircleId) {
            fetchFolders();
        }
        fetchStudyCircles();
    }, [studyCircleId]);

    const fetchFolders = async () => {
        setLoading(true);
        try {
            const response = await api.get(`/api/study-circles/${studyCircleId}/folders`);
            setFolders(response.data);
            // Notify parent component about folder updates
            if (onFoldersUpdate) {
                onFoldersUpdate(response.data);
            }
        } catch (err) {
            setError('Failed to fetch folders');
            console.error('Error fetching folders:', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchStudyCircles = async () => {
        try {
            const response = await api.get('/api/study-circles');
            setStudyCircles(response.data);
        } catch (err) {
            setError('Failed to fetch study circles');
            console.error('Error fetching study circles:', err);
        }
    };

    const handleCreateFolder = async (e) => {
        e.preventDefault();
        setIsCreating(true);
        setError('');
        try {
            const folderData = {
                ...currentFolder,
                study_circle_id: studyCircleId
            };
            await api.post(`/api/study-circles/${studyCircleId}/folders`, folderData);
            setShowCreateModal(false);
            setCurrentFolder({ name: '', description: '' });
            await fetchFolders();
        } catch (err) {
            setError('Failed to create folder');
            console.error('Error creating folder:', err);
        } finally {
            setIsCreating(false);
        }
    };

    const handleEditFolder = async (e) => {
        e.preventDefault();
        setIsEditing(true);
        setError('');
        try {
            const folderData = {
                ...currentFolder,
                study_circle_id: studyCircleId
            };
            await api.put(`/api/study-circles/${studyCircleId}/folders/${currentFolder.id}`, folderData);
            setShowEditModal(false);
            setCurrentFolder({ name: '', description: '' });
            await fetchFolders();
        } catch (err) {
            setError('Failed to update folder');
            console.error('Error updating folder:', err);
        } finally {
            setIsEditing(false);
        }
    };

    const handleDeleteFolder = async (folderId) => {
        if (!window.confirm('Are you sure you want to delete this folder?')) {
            return;
        }

        try {
            await api.delete(`/api/study-circles/${studyCircleId}/folders/${folderId}`);
            await fetchFolders();
        } catch (err) {
            setError('Failed to delete folder');
            console.error('Error deleting folder:', err);
        }
    };

    const openEditModal = (folder) => {
        setCurrentFolder(folder);
        setShowEditModal(true);
    };

    if (!studyCircleId) {
        return <Alert variant="warning">Please select a study circle to view folders.</Alert>;
    }

    if (loading) {
        return <div className="text-center">Loading folders...</div>;
    }

    return (
        <div className="container mt-4">
            <div className="d-flex justify-content-between align-items-end mb-4">
                <h2></h2>
                {user?.role === 'admin' && (
                    <Button variant="primary" onClick={() => setShowCreateModal(true)}>
                        Create Folder
                    </Button>
                )}
            </div>

            {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}

            <div className="row">
                {folders.map(folder => (
                    <div key={folder.id} className="col-md-4 mb-4">
                        <Card>
                            <Card.Body>
                                <Card.Title>{folder.name}</Card.Title>
                                <Card.Text>{folder.description}</Card.Text>
                                {user?.role === 'admin' && (
                                    <div className="d-flex gap-2">
                                        <Button 
                                            variant="primary" 
                                            size="sm"
                                            onClick={() => openEditModal(folder)}
                                        >
                                            Edit
                                        </Button>
                                        <Button 
                                            variant="danger" 
                                            size="sm"
                                            onClick={() => handleDeleteFolder(folder.id)}
                                        >
                                            Delete
                                        </Button>
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                    </div>
                ))}
            </div>

            {/* Create Folder Modal */}
            <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Create New Folder</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleCreateFolder}>
                        <Form.Group className="mb-3">
                            <Form.Label>Name</Form.Label>
                            <Form.Control
                                type="text"
                                value={currentFolder.name}
                                onChange={(e) => setCurrentFolder({ ...currentFolder, name: e.target.value })}
                                required
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Description</Form.Label>
                            <Form.Control
                                as="textarea"
                                value={currentFolder.description}
                                onChange={(e) => setCurrentFolder({ ...currentFolder, description: e.target.value })}
                            />
                        </Form.Group>
                        <div className="d-flex justify-content-end gap-2">
                            <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
                                Cancel
                            </Button>
                            <Button variant="primary" type="submit" disabled={isCreating}>
                                {isCreating ? 'Creating...' : 'Create'}
                            </Button>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>

            {/* Edit Folder Modal */}
            <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Edit Folder</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleEditFolder}>
                        <Form.Group className="mb-3">
                            <Form.Label>Name</Form.Label>
                            <Form.Control
                                type="text"
                                value={currentFolder.name}
                                onChange={(e) => setCurrentFolder({ ...currentFolder, name: e.target.value })}
                                required
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Description</Form.Label>
                            <Form.Control
                                as="textarea"
                                value={currentFolder.description}
                                onChange={(e) => setCurrentFolder({ ...currentFolder, description: e.target.value })}
                            />
                        </Form.Group>
                        <div className="d-flex justify-content-end gap-2">
                            <Button variant="secondary" onClick={() => setShowEditModal(false)}>
                                Cancel
                            </Button>
                            <Button variant="primary" type="submit" disabled={isEditing}>
                                {isEditing ? 'Saving...' : 'Save Changes'}
                            </Button>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>
        </div>
    );
};

export default FolderManager; 